import { debugMessage } from '../common.js'
import { calculatedFields, resolveClusterMasterIndex } from './calculatedFields'

const HAS_INTERNET_FAIL_THRESHOLD = 0.5

const REACT_APP_CLONED_DATA_MODE = process.env.REACT_APP_CLONED_DATA_MODE === 'true'

const POWER_SUM_KEYS = ['gridPower', 'batteryPower', 'aux1Power', 'aux2Power', 'aux3Power']
const SOC_AVG_KEYS = ['socUsable', 'socAverage']

function hasUsableTelemetry(telemetry) {
  return Array.isArray(telemetry) && telemetry.length > 0
}

function sampleKey(sample) {
  return Number(sample?.createdAt)
}

function mergeNumeric(values, { average = false, clonedDivide = false, unitsCount = 1 } = {}) {
  const nums = values.filter((v) => typeof v === 'number' && Number.isFinite(v))
  if (!nums.length) return null
  const sum = nums.reduce((a, b) => a + b, 0)
  if (average) return sum / nums.length
  if (clonedDivide && unitsCount > 0) return sum / unitsCount
  return sum
}

function mergeTelemetryAtIndex(samples, { isCluster, masterIndex, unitsCount }) {
  const first = samples.find((s) => s) || {}
  const merged = {
    createdAt: first.createdAt,
    hardwareIdChanged: samples.some((s) => s?.hardwareIdChanged)
  }

  for (const key of POWER_SUM_KEYS) {
    const values = samples.map((s) => (s ? s[key] : null))
    if (isCluster && (key === 'gridPower' || key === 'aux1Power') && masterIndex != null && samples[masterIndex]) {
      merged[key] = samples[masterIndex][key] ?? null
    } else {
      merged[key] = mergeNumeric(values, {
        clonedDivide: key === 'batteryPower' && REACT_APP_CLONED_DATA_MODE === true,
        unitsCount
      })
    }
  }

  for (const key of SOC_AVG_KEYS) {
    merged[key] = mergeNumeric(
      samples.map((s) => (s ? s[key] : null)),
      { average: true }
    )
  }

  return merged
}

function createClustersAndMergeTelemetry({ unitsData, isCluster }) {
  debugMessage(`Found ${unitsData.length} units`, 'createClustersAndMergeTelemetry()')

  let clusters = {}
  for (let i in unitsData) {
    const unitData = unitsData[i]
    const { unit } = unitData
    let { clusterHash } = unit
    if (isCluster === false || !clusterHash) clusterHash = 'no-cluster'
    if (!clusters[clusterHash]) clusters[clusterHash] = []
    clusters[clusterHash].push(unitData)
  }
  clusters = Object.keys(clusters).map((clusterHash) => clusters[clusterHash])

  debugMessage(`Found ${clusters.length} clusters`, 'createClustersAndMergeTelemetry()')

  for (let i in clusters) {
    const cluster = clusters[i]
    const unitsWithTelemetry = cluster.filter((unitData) => hasUsableTelemetry(unitData.telemetry))

    if (unitsWithTelemetry.length === 0) {
      const units = clusters[i].map((c) => c.unit)
      const { totals } = sumChartData(clusters[i], isCluster)
      clusters[i] = {
        units,
        telemetry: null,
        totals
      }
      continue
    }

    const masterIndex = isCluster
      ? resolveClusterMasterIndex(unitsWithTelemetry.map((unitData) => unitData.unit))
      : 0

    const createdAtSet = new Set()
    for (const unitData of unitsWithTelemetry) {
      for (const sample of unitData.telemetry) {
        const k = sampleKey(sample)
        if (Number.isFinite(k)) createdAtSet.add(k)
      }
    }
    const createdAts = [...createdAtSet].sort((a, b) => a - b)

    const byCreatedAt = unitsWithTelemetry.map((unitData) => {
      const map = new Map()
      for (const sample of unitData.telemetry) {
        const k = sampleKey(sample)
        if (Number.isFinite(k)) map.set(k, sample)
      }
      return map
    })

    const aggregated = createdAts.map((createdAt) => {
      const samples = byCreatedAt.map((map) => map.get(createdAt) || { createdAt })
      return mergeTelemetryAtIndex(samples, {
        isCluster,
        masterIndex,
        unitsCount: unitsWithTelemetry.length
      })
    })

    const units = clusters[i].map((c) => c.unit)
    const { totals } = sumChartData(clusters[i], true)
    clusters[i] = {
      units,
      telemetry: aggregated,
      totals
    }
  }

  return clusters
}

function extendChartData(data) {
  const moment = require('moment')

  const { series } = data || {}

  if (!series) return data

  let pastNow = false

  const chartData = series.map((row, index) => {
    const nextRow = typeof series[index + 1] === 'object' ? series[index + 1] : {}
    const { instant_battery, hasInternetAccess } = row
    const { time: nextRowTime } = nextRow
    let isNow = false

    if (nextRowTime && !pastNow && moment(nextRowTime).isAfter()) {
      isNow = true
      pastNow = true
    }

    return {
      ...row,
      isNow,
      missingData: instant_battery === null && !pastNow,
      noInternet: typeof hasInternetAccess === 'number' && hasInternetAccess < HAS_INTERNET_FAIL_THRESHOLD && !pastNow
    }
  })

  return {
    ...data,
    series: [...chartData]
  }
}

/** Same missingData rules as Charts (extendChartData); share of intervals with gaps, excluding future rows. */
function getChartMissingDataFraction(unitsData) {
  const { series } = extendChartData(calculatedFields(unitsData)) || {}
  if (!series?.length) return 0

  let missingCount = 0
  for (const row of series) {
    if (row.missingData) missingCount++
  }
  return missingCount / series.length
}

function sumChartData(data, isCluster) {
  const master_i = isCluster ? resolveClusterMasterIndex(data.map((row) => row.unit)) : 0

  const isNumber = (n) => (Number(n) === n && n % 1 === 0) || (Number(n) === n && n % 1 !== 0)

  const acc = { series: [], totals: {}, units: [] }

  const numberOfUnits = data.length

  let lastClusterHash = null

  for (const i in data) {
    const { series, totals, unit } = data[i]
    let { units } = data[i]
    if (unit) units = [unit]

    if (units?.[0]?.clusterHash !== lastClusterHash) {
      lastClusterHash = units?.[0]?.clusterHash
    }

    for (const measurementName in totals || {}) {
      let firstOnly = false

      const isAccValueMissing = (name) => acc.totals[name] === undefined || acc.totals[name] === null

      switch (measurementName) {
        case 'gridExports':
        case 'gridImports':
        case 'solarConsumed':
        case 'solarGenerated':
          firstOnly = !(i == master_i)
          if (isAccValueMissing(measurementName)) acc.totals[measurementName] = 0
          break
        case 'homeConsumed':
          if (isAccValueMissing(measurementName)) acc.totals[measurementName] = 0
          firstOnly = !(i == master_i)
          break
        case 'batteryDischarge':
          if (isAccValueMissing('homeConsumed')) acc.totals['homeConsumed'] = 0
          if (!(i == master_i)) acc.totals['homeConsumed'] += totals[measurementName]
          if (isAccValueMissing(measurementName)) acc.totals[measurementName] = 0
          break
        case 'batteryCharge':
          if (isAccValueMissing('homeConsumed')) acc.totals['homeConsumed'] = 0
          if (!(i == master_i)) acc.totals['homeConsumed'] -= totals[measurementName]
          if (isAccValueMissing(measurementName)) acc.totals[measurementName] = 0
          break
        case 'batteryOutputExported':
          if (isAccValueMissing('solarConsumed')) acc.totals['solarConsumed'] = 0
          if (!(i == master_i)) acc.totals['solarConsumed'] -= totals[measurementName]
          if (isAccValueMissing(measurementName)) acc.totals[measurementName] = 0
          break
        default:
          if (isAccValueMissing(measurementName)) acc.totals[measurementName] = 0
      }

      if (isNumber(totals[measurementName]) && isNumber(acc.totals[measurementName])) {
        if (isCluster && firstOnly === true) continue
        acc.totals[measurementName] += totals[measurementName]
      }
    }

    for (const j in units || []) {
      if (acc.units === undefined) acc.units = []
      acc.units.push(units[j])
    }

    if (!series) continue

    for (const j in series) {
      const measurement = series[j]

      for (const measurementName in measurement) {
        let firstOnly = false

        if (acc.series[j] === undefined) acc.series[j] = {}

        const isAccValueMissing = (name) => acc.series[j][name] === undefined || acc.series[j][name] === null

        switch (measurementName) {
          case 'batteryInputFromGrid':
          case 'batteryInputFromSolar':
          case 'gridConsumedByHome':
          case 'instant_battery':
          case 'instant_grid':
          case 'instant_solar':
            firstOnly = !(i == master_i)
            if (isAccValueMissing(measurementName)) acc.series[j][measurementName] = 0
            break
          case 'homeConsumed':
            if (isAccValueMissing(measurementName)) acc.series[j][measurementName] = 0
            firstOnly = !(i == master_i)
            break
          case 'batteryDischarge':
            if (isAccValueMissing('homeConsumed')) acc.series[j]['homeConsumed'] = 0
            acc.series[j]['homeConsumed'] += measurement[measurementName]
            firstOnly = true
            break
          case 'batteryCharge':
            if (isAccValueMissing('homeConsumed')) acc.series[j]['homeConsumed'] = 0
            acc.series[j]['homeConsumed'] -= measurement[measurementName]
            firstOnly = true
            break
          case 'batteryOutputConsumedByHome':
          case 'batteryOutputExported':
          case 'instant_demand':
          case 'instant_soc':
          case 'accessoryClamp1':
          case 'gridClampDisconnected':
          case 'dnoOverride':
          case 'epsGridLoss':
          case 'epsLowSoC':
          case 'epsOverConsumption':
          case 'hasInternetAccess':
            if (isAccValueMissing(measurementName)) acc.series[j][measurementName] = 0
            break
          default:
            if (isAccValueMissing(measurementName)) acc.series[j][measurementName] = measurement[measurementName]
        }

        if (isNumber(measurement[measurementName]) && isNumber(acc.series[j][measurementName])) {
          if (firstOnly === true) continue
          switch (measurementName) {
            case 'instant_soc':
              acc.series[j][measurementName] += measurement[measurementName] / numberOfUnits
              break
            default:
              acc.series[j][measurementName] += measurement[measurementName]
          }
        }
      }
    }
  }

  return acc
}

function getChartData({ unitsData }) {
  if (!Array.isArray(unitsData) || !unitsData.length) {
    return { singles: [], clusters: [], isCluster: false }
  }

  const { clusterHash } = unitsData[0].unit
  const isCluster = typeof clusterHash === 'string'
  debugMessage(`${isCluster ? 'Clustering IS selected' : 'Clustering is NOT selected'}`, 'getChartData()')

  const singles = unitsData.map((ud) => extendChartData(calculatedFields(ud)))

  const clustersTelemetry = createClustersAndMergeTelemetry({ unitsData, isCluster })

  const clusters = clustersTelemetry.map((crd) => {
    return extendChartData(calculatedFields(crd))
  })

  return {
    singles,
    clusters,
    isCluster
  }
}

function findClusters(unitsData) {
  const foundClusters = {}

  if (!unitsData[0] || !unitsData[0].unit) {
    return []
  } else {
    for (const i in unitsData) {
      let clusterHash = unitsData[i].unit.clusterHash ? unitsData[i].unit.clusterHash : 'no-cluster'

      if (!foundClusters[clusterHash]) {
        foundClusters[clusterHash] = []
      }

      foundClusters[clusterHash].push(unitsData[i].unit)
    }

    return Object.keys(foundClusters).map((k) => foundClusters[k])
  }
}

export { getChartData, sumChartData, findClusters, getChartMissingDataFraction }
